// DatasetPane.jsx
import React, { useEffect, useMemo, useRef, useState, forwardRef, useImperativeHandle } from "react";
import "./datasetPane.scss";
import {AISplitPane} from "../../../components/AIChat/AISplitPane"
import {
  ToastNotifications,
  FormFieldsControl,
  ButtonControl,
  Loading,
} from "spark-core-dx/components";

import { useGenericManager } from "spark-core-dx/hooks";
import { ItemServiceFactory } from "../../../services/ItemServiceFactory";

// local
import MiniTableControl from "./MiniTableControl";

/* =============================================================================
   DatasetPane
   - Everything that used to be inside PanelControl.
   - Parent (DatasetPanel) wraps this with PanelControl and calls ref.save().
   - Provides callbacks to parent to reflect "ready" and "saving" states.
   ========================================================================== */

const EMPTY_GUID = "00000000-0000-0000-0000-000000000000";

// Fields that live under the left-side Manual / Filter tabs
const LEFT_COMPLEX_FIELDS = ["TemplateQuery", "SupportedVisuals", "ConvokeQuery"];
const COLUMNS_FIELD = "Columns";

// Columns shown in MiniTableControl
const MINI_COLUMNS = [
  { key: "Name",         title: "Name" },
  { key: "DisplayName",  title: "Display Name" },
  { key: "Format",       title: "Format" },
  { key: "DataTypeName", title: "Data Type" },
  { key: "Sequence",     title: "Seq" },
];

const DatasetPane = forwardRef(function DatasetPane(
  {
    datasetId,
    // AI chat configuration (AISplitPane will mount chat in this pane)
    chatPane = "left",
    operationName = "AIDatasetBuilder",
    referenceName = "dataset",
    chatParams = {},

    // Split-pane sizing
    splitStorageKey = "dataset-panel:left-width",
    splitInitialRightPct = 0.33,
    splitMinLeft = 260,
    splitMinRight = 360,

    // Callbacks to parent (for header button state, etc.)
    onReadyChange,        // boolean → panel can enable Save
    onSavingChange,       // boolean → disable Save while true
  },
  ref
) {
  const itemServiceFactory = useMemo(() => new ItemServiceFactory(), []);
  const datasetMgr = useGenericManager(
    itemServiceFactory.ItemServiceEnum.DashboardDataset,
    true
  );

  const [toast, setToast] = useState(null);
  const [metadata, setMetadata] = useState(null);
  const [dataset, setDataset] = useState(null);
  const [loadingMeta, setLoadingMeta] = useState(false);
  const [loadingItem, setLoadingItem] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Right-pane preview data
  const [rawRows, setRawRows] = useState([]);
  const [resultRows, setResultRows] = useState([]);

  // Notify parent when ready/saving changes
  useEffect(() => { onSavingChange?.(!!saving); }, [saving, onSavingChange]);
  useEffect(() => {
    const ready = !!(metadata && dataset && !loadingMeta && !loadingItem);
    onReadyChange?.(ready);
  }, [metadata, dataset, loadingMeta, loadingItem, onReadyChange]);

  // Load metadata + dataset
  useEffect(() => {
    let alive = true;

    const loadMetadata = async () => {
      if (metadata) return;
      setLoadingMeta(true);
      try {
        const resp = await datasetMgr.GetMetadata({});
        const items = resp?.Items ?? [];
        const first = items[0] || null;
        if (alive) setMetadata(first);
      } catch (err) {
        if (alive) {
          setMetadata(null);
          setError("Failed to load dataset metadata.");
          console.error("Dataset metadata load failed:", err);
        }
      } finally {
        if (alive) setLoadingMeta(false);
      }
    };

    const loadDataset = async () => {
      if (!datasetId) return;
      setLoadingItem(true);
      try {
        if (datasetId === EMPTY_GUID) {
          const blank = {
            _id: EMPTY_GUID,
            IsNew: true,
            Name: "",
            Description: "",
            DataSourceId: null,
            TemplateQuery: "",
            SupportedVisuals: [],
            ConvokeQuery: null,
            Columns: [],
            IsActive: true,
          };
          if (alive) setDataset(blank);
        } else {
          const resp = await datasetMgr.getItem(datasetId);
          const item =
            resp?.Items?.first?.() ||
            (Array.isArray(resp?.Items) ? resp.Items[0] : resp?.Items) ||
            null;
          if (alive) setDataset(item);
        }
      } catch (err) {
        if (alive) {
          setDataset(null);
          setError("Failed to load dataset.");
          console.error("Dataset load failed:", err);
        }
      } finally {
        if (alive) setLoadingItem(false);
      }
    };

    loadMetadata();
    loadDataset();

    return () => { alive = false; };
  }, [datasetId, datasetMgr, metadata]);

  // Imperative API for parent
  const doSave = async () => {
    if (!dataset) return;
    setSaving(true);
    setError(null);
    try {
      const resp = await datasetMgr.saveItem(dataset);
      if (!resp?.Success) throw new Error(resp?.DisplayMessage || "Save failed");
      const saved =
        resp?.Items?.first?.() ||
        (Array.isArray(resp?.Items) ? resp.Items[0] : resp?.Items) ||
        null;
      if (saved) setDataset(saved);
      setToast("Dataset saved.");
    } catch (err) {
      console.error("Save failed:", err);
      setError("Save failed. See console for details.");
    } finally {
      setSaving(false);
    }
  };

  useImperativeHandle(ref, () => ({
    save: doSave,
    getDataset: () => dataset,
    setDataset, // optional: allow parent to inject/poke if needed
  }));

  // Update a field on the working copy
  const updateField = (fieldName, value) => {
    setDataset((prev) => ({ ...(prev || {}), [fieldName]: value }));
  };

  // Field bucketing
  const allFieldNames = useMemo(() => {
    const fields = metadata?.Fields || metadata?.fields || [];
    return fields.map((f) => f.FieldName || f.fieldName || f.Name).filter(Boolean);
  }, [metadata]);

  const optionFieldNames = useMemo(() => {
    if (!allFieldNames) return [];
    const ignore = new Set([...LEFT_COMPLEX_FIELDS, COLUMNS_FIELD]);
    return allFieldNames.filter((n) => !ignore.has(n));
  }, [allFieldNames]);

  // Columns helpers
  const setColumns = (cols) => updateField("Columns", cols);
  const getColumns = () => dataset?.Columns || [];

  // -------- LEFT TABS (arrays consumed by AISplitPane) -----------------------

  const LeftManual = () => {
    if (loadingMeta || loadingItem) return <Loading />;
    if (error) return <div className="error">{error}</div>;
    if (!metadata || !dataset) return <div className="muted">No dataset loaded.</div>;

    const showSupported = allFieldNames?.includes("SupportedVisuals");

    return (
      <div className="dataset-left-manual">
        <section className="block">
          <FormFieldsControl
            metadata={metadata}
            item={dataset}
            fieldNames={["TemplateQuery"]}
            onChange={(value, fieldName) => updateField(fieldName, value)}
          />
          <div className="hint">/* TODO: replace TemplateQuery control with shared SQL editor */</div>
        </section>

        {showSupported && (
          <section className="block">
            <FormFieldsControl
              metadata={metadata}
              item={dataset}
              fieldNames={["SupportedVisuals"]}
              onChange={(value, fieldName) => updateField(fieldName, value)}
            />
          </section>
        )}
      </div>
    );
  };

  const LeftFilter = () => {
    if (!metadata || !dataset) return <Loading />;
    const hasField = allFieldNames?.includes("ConvokeQuery");
    return (
      <div className="dataset-left-filter">
        {hasField ? (
          <FormFieldsControl
            metadata={metadata}
            item={dataset}
            fieldNames={["ConvokeQuery"]}
            onChange={(value, fieldName) => updateField(fieldName, value)}
          />
        ) : (
          <div className="placeholder">
            <em>ConvokeQuery metadata not found. Add field to metadata to enable filter UI.</em>
          </div>
        )}
      </div>
    );
  };

  // NOTE: AISplitPane will replace the "AI" tab's content when chatPane === "left"
  const leftTabs = [
    { key: "ai",     title: "AI",     content: <div className="muted">Loading assistant…</div> },
    { key: "manual", title: "Manual", content: <LeftManual /> },
    { key: "filter", title: "Filter", content: <LeftFilter /> },
  ];

  // -------- MIDDLE TABS ------------------------------------------------------

  const MiddleOptions = () => {
    if (!metadata || !dataset) return <Loading />;
    return (
      <div className="dataset-options">
        <FormFieldsControl
          metadata={metadata}
          item={dataset}
          fieldNames={optionFieldNames}
          onChange={(value, fieldName) => updateField(fieldName, value)}
        />
      </div>
    );
  };

  const MiddleColumns = () => {
    const rows = getColumns();
    const handleRowsChange = (nextRows) => setColumns(nextRows);
    return (
      <div className="dataset-columns">
        <MiniTableControl
          title="Columns"
          rows={rows}
          columns={MINI_COLUMNS}
          editable={true}
          onRowsChange={handleRowsChange}
          // columnFieldMetadata={metadata?.Nested?.Columns ?? null} // if you have nested metadata
        />
      </div>
    );
  };

  const middleTabs = [
    { key: "options", title: "Options", content: <MiddleOptions /> },
    { key: "columns", title: "Columns", content: <MiddleColumns /> },
  ];

  // -------- RIGHT TABS (raw/result/visual) ----------------------------------

  const loadRawPreview = async () => {
    try {
      // const resp = await datasetMgr.RunOperation({ operationName: "PreviewRaw", item: dataset, isItem: true });
      // setRawRows(resp?.Items ?? []);
      setRawRows([]); // placeholder
    } catch (err) {
      console.error("PreviewRaw failed:", err);
      setRawRows([]);
    }
  };

  const loadResultPreview = async () => {
    try {
      // const resp = await datasetMgr.RunOperation({ operationName: "PreviewQuery", item: dataset, isItem: true });
      // setResultRows(resp?.Items ?? []);
      setResultRows([]); // placeholder
    } catch (err) {
      console.error("PreviewQuery failed:", err);
      setResultRows([]);
    }
  };

  useEffect(() => {
    if (!dataset) return;
    loadRawPreview();
    loadResultPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datasetId, dataset?.TemplateQuery, JSON.stringify(dataset?.Columns || [])]);

  const RightRaw = () => (
    <div className="dataset-right-raw">
      <MiniTableControl title="Raw Data" rows={rawRows} columns={MINI_COLUMNS} editable={false} />
    </div>
  );

  const RightResult = () => (
    <div className="dataset-right-result">
      <MiniTableControl title="Result Data" rows={resultRows} columns={MINI_COLUMNS} editable={false} />
    </div>
  );

  const RightVisual = () => (
    <div className="dataset-right-visual">
      {/* TODO: mount DynamicChart with resultRows */}
      <div className="muted">Visual preview not wired yet.</div>
      <div className="hint">/* TODO: mount DynamicChart with resultRows */</div>
    </div>
  );

  const rightTabs = [
    { key: "raw",    title: "Raw Data",    content: <RightRaw /> },
    { key: "result", title: "Result Data", content: <RightResult /> },
    { key: "visual", title: "Visual",      content: <RightVisual /> },
  ];

  return (
    <div className="dataset-pane">
      {toast && <ToastNotifications position="top" message={toast} />}

      <AISplitPane
        storageKey={splitStorageKey}
        initialRightPct={splitInitialRightPct}
        minLeft={splitMinLeft}
        minRight={splitMinRight}
        chatPane={chatPane}
        operationName={operationName}
        referenceId={dataset?._id || datasetId}
        referenceName={referenceName}
        params={chatParams}
        left={leftTabs}
        middle={middleTabs}
        right={rightTabs}
      />
    </div>
  );
});

export default DatasetPane;
